import {
    compareDrivesByStartDateDesc,
    readStartDateAsDate,
} from './driveStartDate';

export const ACTIVE_DRIVE_STATUSES = ['accepted', 'started'];

const driveIdNumber = (drive) => {
    const id = drive?.drive_id ?? drive?.fk_drive_id;
    const parsed = Number(id);
    return Number.isFinite(parsed) ? parsed : 0;
};

const driveDriverId = (drive) =>
    drive?.fk_driver_id ?? drive?.driver_id ?? null;

/** Started ride first, then oldest start (that ride finishes before the next). */
const compareCurrentDriveFirst = (a, b) => {
    const aStarted = a?.status === 'started' ? 0 : 1;
    const bStarted = b?.status === 'started' ? 0 : 1;
    if (aStarted !== bStarted) return aStarted - bStarted;

    const aTime = readStartDateAsDate(a)?.getTime();
    const bTime = readStartDateAsDate(b)?.getTime();
    if (aTime != null && bTime != null && aTime !== bTime) {
        return aTime - bTime;
    }
    if (aTime != null && bTime == null) return -1;
    if (aTime == null && bTime != null) return 1;

    return driveIdNumber(a) - driveIdNumber(b);
};

export const getActiveDriveForDriver = (drives, driver) => {
    if (!driver) {
        return undefined;
    }

    return getActiveDriveForDriverId(
        drives,
        driver.driver_id ?? driver.fk_driver_id
    );
};

export const getActiveDrivesForDriverId = (drives, driverId) => {
    if (driverId == null || !Array.isArray(drives)) {
        return [];
    }

    const driverKey = String(driverId);

    return drives.filter(
        (drive) =>
            String(drive.driver_id ?? drive.fk_driver_id) === driverKey &&
            ACTIVE_DRIVE_STATUSES.includes(drive.status)
    );
};

export const getActiveDriveForDriverId = (drives, driverId) => {
    return getActiveDrivesForDriverId(drives, driverId)[0];
};

export const getCarAssignmentForDriver = (carAssignments, driver) => {
    if (!driver || !Array.isArray(carAssignments)) {
        return null;
    }

    return (
        carAssignments.find(
            (row) => String(row.driver_id) === String(driver.driver_id)
        ) ?? null
    );
};

/**
 * One row per driver: current active ride plus optional next queued ride.
 * @param {Array} drives
 * @returns {Array<{ driverKey: string|null, current: object, next: object|null }>}
 */
export const groupActiveDrivesByDriver = (drives) => {
    if (!Array.isArray(drives)) return [];

    const byDriver = new Map();
    let orphanIndex = 0;

    drives.forEach((drive) => {
        if (!ACTIVE_DRIVE_STATUSES.includes(drive.status)) return;

        const driverId = driveDriverId(drive);
        const key =
            driverId == null ? `__orphan_${orphanIndex++}` : String(driverId);
        const list = byDriver.get(key);
        if (list) list.push(drive);
        else byDriver.set(key, [drive]);
    });

    const groups = [];
    byDriver.forEach((driverDrives, key) => {
        const sorted = [...driverDrives].sort(compareCurrentDriveFirst);
        groups.push({
            driverKey: key.startsWith('__orphan_') ? null : key,
            current: sorted[0],
            next: sorted[1] ?? null,
        });
    });

    groups.sort((a, b) => compareDrivesByStartDateDesc(a.current, b.current));
    return groups;
};
